import { useState } from 'react';
import { addMonths, format } from 'date-fns';
import clsx from 'clsx';
import { Button } from './Button';
import { INTEREST_LEVELS, PAUSE_LEVEL } from './venueConstants';
import { nextMonthStart } from '../../utils/venuePause';
import type { InterestLevel, Recontact, RecontactRequest } from '../../types';

/**
 * The presets of the pause picker, in the order they are offered: the short offsets a venue gives
 * off the cuff, then the seasonal answers — "come back for the new season" is a month, not a number
 * of months. `resolve` only previews the date; the server computes the one it stores
 * (`resolve_after`), so the two must agree. Legacy `3mo` / `9mo` / `1y` pauses still parse and
 * still display; they are simply no longer offered. See docs/VENUE_PAUSE.md.
 */
const PRESETS: { duration: Recontact['duration']; label: string; resolve: (now: Date) => Date }[] = [
  { duration: '1mo', label: '1 month', resolve: now => addMonths(now, 1) },
  { duration: '2mo', label: '2 months', resolve: now => addMonths(now, 2) },
  { duration: '6mo', label: '6 months', resolve: now => addMonths(now, 6) },
  { duration: '2y', label: '2 years', resolve: now => addMonths(now, 24) },
  { duration: 'next_september', label: 'Next September', resolve: now => nextMonthStart(9, now) },
  { duration: 'next_january', label: 'Next January', resolve: now => nextMonthStart(1, now) },
  { duration: 'next_june', label: 'Next June', resolve: now => nextMonthStart(6, now) },
];

/**
 * Which button an existing pause reopens on. A pause set with a preset that is no longer offered
 * reopens as *Pick a date* on its stored date: the date is real and has to stay visible, and
 * re-saving keeps it rather than silently re-counting months from today.
 */
function initialDuration(recontact: Recontact | null): Recontact['duration'] | null {
  if (!recontact) return null;
  const offered = recontact.duration === 'custom' || PRESETS.some(p => p.duration === recontact.duration);
  return offered ? recontact.duration : 'custom';
}

/** What a preset works out to. The server computes the stored date; this is only to show it. */
function previewDate(duration: Recontact['duration'], custom: string): Date | null {
  const preset = PRESETS.find(p => p.duration === duration);
  if (preset) return preset.resolve(new Date());
  if (!custom) return null;
  const date = new Date(`${custom}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

interface InterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  bandLabel: string;
  currentLevel: InterestLevel;
  currentComment: string;
  /**
   * The All-Bands entry, the only one a pause can hang off: a "Not Now" on one band is that
   * band's story, not a promise the venue made.
   */
  isOverall?: boolean;
  currentRecontact?: Recontact | null;
  /** Pause-only: the level is fixed to "Not Now" and its picker hidden (bulk / row menu). */
  dateOnly?: boolean;
  /** `recontact` is undefined for a per-band entry, and otherwise the value to store (or clear). */
  onSave: (level: InterestLevel, comment: string, recontact?: RecontactRequest | null) => void;
}

export function InterestModal({
  isOpen,
  onClose,
  bandLabel,
  currentLevel,
  currentComment,
  isOverall = false,
  currentRecontact = null,
  dateOnly = false,
  onSave,
}: InterestModalProps) {
  const [level, setLevel] = useState<InterestLevel>(dateOnly ? PAUSE_LEVEL : currentLevel);
  const [comment, setComment] = useState(currentComment);
  const [duration, setDuration] = useState<Recontact['duration'] | null>(() => initialDuration(currentRecontact));
  const [customDate, setCustomDate] = useState(
    currentRecontact?.after ? format(new Date(currentRecontact.after), 'yyyy-MM-dd') : '',
  );

  if (!isOpen) return null;

  const pausing = isOverall && level === PAUSE_LEVEL;
  const preview = duration ? previewDate(duration, customDate) : null;

  const save = () => {
    let recontact: RecontactRequest | null | undefined;
    if (isOverall) {
      // Always explicit on the All-Bands entry: the server keeps the pause and the badge in step,
      // and an omitted field would mean "leave it alone" instead of "there is no date".
      recontact =
        pausing && duration && (duration !== 'custom' || customDate)
          ? { duration, after: duration === 'custom' ? `${customDate}T00:00:00Z` : undefined, reason: comment || undefined }
          : null;
    }
    onSave(level, comment, recontact);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-dark-surface border border-dark-border rounded-lg p-4 max-w-sm w-full" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-dark-text-primary mb-3">
          {dateOnly ? `Pause: ${bandLabel}` : `Interest: ${bandLabel}`}
        </h3>
        <div className="space-y-3">
          {!dateOnly && (
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">Level</label>
              <div className="flex flex-wrap gap-1">
                {INTEREST_LEVELS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setLevel(opt.value)}
                    className={clsx(
                      'px-2 py-1 rounded-sm text-xs font-medium border transition-colors',
                      level === opt.value
                        ? `${opt.bgClass} ${opt.color} ring-1 ring-offset-1 ring-offset-dark-surface ring-current`
                        : 'bg-dark-elevated border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {pausing && (
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1.5">
                Don't recontact before
              </label>
              <div className="flex flex-wrap gap-1">
                {PRESETS.map(preset => (
                  <button
                    key={preset.duration}
                    onClick={() => setDuration(preset.duration)}
                    className={clsx(
                      'px-2 py-1 rounded-sm text-xs font-medium border transition-colors',
                      duration === preset.duration
                        ? 'bg-orange-900/30 border-orange-700 text-orange-400 ring-1 ring-offset-1 ring-offset-dark-surface ring-current'
                        : 'bg-dark-elevated border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  onClick={() => setDuration('custom')}
                  className={clsx(
                    'px-2 py-1 rounded-sm text-xs font-medium border transition-colors',
                    duration === 'custom'
                      ? 'bg-orange-900/30 border-orange-700 text-orange-400 ring-1 ring-offset-1 ring-offset-dark-surface ring-current'
                      : 'bg-dark-elevated border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
                  )}
                >
                  Pick a date
                </button>
                {duration && (
                  <button
                    onClick={() => { setDuration(null); setCustomDate(''); }}
                    className="px-2 py-1 rounded-sm text-xs font-medium border border-dark-border bg-dark-elevated text-dark-text-muted hover:text-dark-text-primary transition-colors"
                  >
                    No date
                  </button>
                )}
              </div>
              {duration === 'custom' && (
                <input
                  type="date"
                  value={customDate}
                  min={format(addMonths(new Date(), 0), 'yyyy-MM-dd')}
                  onChange={e => setCustomDate(e.target.value)}
                  className="input w-full text-sm mt-1.5"
                />
              )}
              <p className="text-xs text-dark-text-muted mt-1.5">
                {preview
                  ? `Hidden from the venues list until ${format(preview, 'MMM d, yyyy')}, and left out of campaigns. A todo will bring it back.`
                  : 'Without a date the venue stays visible and campaigns still reach it.'}
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">
              {pausing ? 'Reason' : 'Comment'}
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={pausing ? 'What they said...' : 'Optional comment...'}
              rows={2}
              className="input w-full resize-y min-h-12 text-sm"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={save}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
